import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/layout/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Check,
  X,
  Zap,
  Crown,
  Building2,
  Sparkles,
  MapPin,
  Bot,
  Brain,
  Users,
  BarChart3,
  Calendar,
  FileText,
  Star
} from 'lucide-react';

const plans = [
  {
    id: 'basic',
    name: 'Starter',
    price: 699,
    period: 'month',
    description: 'Essential AI features for small teams',
    icon: Zap,
    color: 'blue',
    popular: false,
    features: [
      { name: 'Task Management', description: 'Create, assign, track tasks', included: true, icon: Check },
      { name: 'Check-in/Check-out', description: 'Geo-tagged attendance', included: true, icon: Check },
      { name: 'Calendar & iCal', description: 'Schedule management', included: true, icon: Calendar },
      { name: 'Nearby Task Scanner', description: 'Find tasks near you', included: true, icon: MapPin },
      { name: 'GPS Live Tracking', description: 'Real-time field tracking', included: false, icon: MapPin },
      { name: 'Smart Task Prioritization', description: 'AI task ranking', included: false, icon: Brain },
      { name: 'AI Sales Assistant', description: '24/7 AI help', included: false, icon: Bot },
      { name: 'Customer Intelligence', description: 'AI customer insights', included: false, icon: Users },
      { name: 'Smart Visit Planning', description: 'AI schedule optimization', included: false, icon: Calendar },
    ],
    limits: {
      users: 50,
      tasks: 500,
      aiRequests: '100/day'
    }
  },
  {
    id: 'pro',
    name: 'Professional',
    price: 1499,
    period: 'month',
    description: 'Advanced AI for growing businesses',
    icon: Crown,
    color: 'purple',
    popular: true,
    features: [
      { name: 'Task Management', description: 'Create, assign, track tasks', included: true, icon: Check },
      { name: 'Check-in/Check-out', description: 'Geo-tagged attendance', included: true, icon: Check },
      { name: 'Calendar & iCal', description: 'Schedule management', included: true, icon: Calendar },
      { name: 'Nearby Task Scanner', description: 'Full scan with distances', included: true, icon: MapPin },
      { name: 'GPS Live Tracking', description: 'Real-time field tracking', included: true, icon: MapPin },
      { name: 'Smart Task Prioritization', description: 'AI task ranking', included: true, icon: Brain },
      { name: 'AI Sales Assistant', description: '24/7 AI help', included: false, icon: Bot },
      { name: 'Customer Intelligence', description: 'AI customer insights', included: false, icon: Users },
      { name: 'Smart Visit Planning', description: 'AI schedule optimization', included: false, icon: Calendar },
    ],
    limits: {
      users: 150,
      tasks: 2000,
      aiRequests: '500/day'
    }
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 2499,
    period: 'month',
    description: 'Full AI suite for large organizations',
    icon: Building2,
    color: 'amber',
    popular: false,
    features: [
      { name: 'Task Management', description: 'Create, assign, track tasks', included: true, icon: Check },
      { name: 'Check-in/Check-out', description: 'Geo-tagged attendance', included: true, icon: Check },
      { name: 'Calendar & iCal', description: 'Schedule management', included: true, icon: Calendar },
      { name: 'Nearby Task Scanner', description: 'Full scan with distances', included: true, icon: MapPin },
      { name: 'GPS Live Tracking', description: 'Real-time field tracking', included: true, icon: MapPin },
      { name: 'Smart Task Prioritization', description: 'AI task ranking', included: true, icon: Brain },
      { name: 'AI Sales Assistant', description: '24/7 AI help', included: true, icon: Bot },
      { name: 'Customer Intelligence', description: 'AI customer insights', included: true, icon: Users },
      { name: 'Performance Analytics', description: 'AI daily summaries', included: true, icon: BarChart3 },
      { name: 'Smart Visit Planning', description: 'AI schedule optimization', included: true, icon: Calendar },
      { name: 'Smart Notes', description: 'AI-enhanced documentation', included: true, icon: FileText },
    ],
    limits: {
      users: 'Unlimited',
      tasks: 'Unlimited',
      aiRequests: 'Unlimited'
    }
  }
];

const aiFeatures = [
  {
    id: 'ai_assistant',
    name: 'AI Sales Assistant',
    description: 'Your personal AI companion that answers questions, provides sales tips, and helps with any field operation queries 24/7.',
    icon: Bot,
    tier: 'basic',
    benefits: ['24/7 availability', 'Context-aware responses', 'Sales best practices']
  },
  {
    id: 'task_prioritization',
    name: 'Smart Task Prioritization',
    description: 'AI analyzes deadlines, customer value, and revenue potential to automatically rank and prioritize your daily tasks for maximum impact.',
    icon: Brain,
    tier: 'pro',
    benefits: ['Revenue-based scoring', 'Risk alerts', 'Deadline tracking']
  },
  {
    id: 'customer_insights',
    name: 'Customer Intelligence',
    description: 'Deep AI analysis of customer behavior, visit history, and engagement patterns to identify upsell opportunities and relationship health.',
    icon: Users,
    tier: 'pro',
    benefits: ['Engagement scoring', 'Upsell detection', 'Churn prediction']
  },
  {
    id: 'daily_summary',
    name: 'Performance Analytics',
    description: 'AI-generated daily and weekly performance reports with productivity scores, highlights, and actionable improvement suggestions.',
    icon: BarChart3,
    tier: 'pro',
    benefits: ['Productivity scoring', 'Team benchmarks', 'Trend analysis']
  },
  {
    id: 'visit_planning',
    name: 'Smart Visit Planning',
    description: 'AI creates your entire day schedule, optimizing for geography, customer priority, and realistic time allocations including breaks.',
    icon: Calendar,
    tier: 'enterprise',
    benefits: ['Full day scheduling', 'Buffer time management', 'Backup visit suggestions']
  },
  {
    id: 'smart_notes',
    name: 'Smart Notes',
    description: 'Transform rough field notes into structured documentation with auto-extracted action items, sentiment analysis, and smart tagging.',
    icon: FileText,
    tier: 'enterprise',
    benefits: ['Sentiment detection', 'Auto action items', 'Smart categorization']
  }
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currency, setCurrency] = useState('INR');
  const [exchangeRate, setExchangeRate] = useState(84.5);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exchange/rate`)
      .then(r => r.json())
      .then(d => setExchangeRate(d.usd_to_inr))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPrice = (price) => {
    let p = annual ? Math.round(price * 10) : price;
    if (currency === 'USD') return Math.round((p / exchangeRate) * 100) / 100;
    return p;
  };

  const formatPrice = (price) => {
    if (currency === 'USD') return `$${price < 10 ? price.toFixed(2) : Math.round(price)}`;
    return `₹${Math.round(price).toLocaleString('en-IN')}`;
  };

  const getPeriod = () => annual ? 'year' : 'month';

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    toast.success(`${planId.charAt(0).toUpperCase() + planId.slice(1)} plan selected! Contact us to activate.`);
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'basic': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pro': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'enterprise': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <MainLayout>
      <div className="space-y-12 pb-12" data-testid="pricing-page">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-4">
            <Sparkles className="w-3 h-3 mr-1" />
            AI-Powered Plans
          </Badge>
          <h1 className="text-4xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Choose Your AI Power Level
          </h1>
          <p className="text-lg text-gray-600">
            Unlock powerful AI features designed to supercharge your team's productivity and efficiency.
          </p>
          
          {/* Billing Toggle */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8">
            <div className="flex items-center gap-4">
              <span className={`text-sm font-medium ${!annual ? 'text-gray-900' : 'text-gray-500'}`}>Monthly</span>
              <Switch
                checked={annual}
                onCheckedChange={setAnnual}
                data-testid="billing-toggle"
              />
              <span className={`text-sm font-medium ${annual ? 'text-gray-900' : 'text-gray-500'}`}>
                Annual
                <Badge className="ml-2 bg-green-100 text-green-700 border-green-200 text-xs">
                  Save 17%
                </Badge>
              </span>
            </div>
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5" data-testid="pricing-currency-toggle">
              <button
                onClick={() => setCurrency('INR')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${currency === 'INR' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                ₹ INR
              </button>
              <button
                onClick={() => setCurrency('USD')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${currency === 'USD' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'}`}
              >
                $ USD
              </button>
            </div>
          </div>
          {currency === 'USD' && (
            <p className="text-xs text-gray-400 mt-2">Live rate: 1 USD = ₹{exchangeRate}</p>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan) => {
            const Icon = plan.icon;
            return (
              <Card
                key={plan.id}
                className={`relative border-2 transition-all hover:shadow-lg ${
                  plan.popular
                    ? 'border-purple-500 shadow-purple-100'
                    : 'border-gray-200 hover:border-blue-200'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-purple-600 text-white border-purple-600">
                      <Star className="w-3 h-3 mr-1" />
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-2">
                  <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                    plan.color === 'blue' ? 'bg-blue-100' :
                    plan.color === 'purple' ? 'bg-purple-100' : 'bg-amber-100'
                  }`}>
                    <Icon className={`w-7 h-7 ${
                      plan.color === 'blue' ? 'text-blue-600' :
                      plan.color === 'purple' ? 'text-purple-600' : 'text-amber-600'
                    }`} />
                  </div>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Outfit' }}>{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                        {formatPrice(getPrice(plan.price))}
                      </span>
                      <span className="text-gray-500 ml-2">/{getPeriod()}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">per user</p>
                  </div>

                  {/* Limits */}
                  <div className="grid grid-cols-3 gap-2 mb-6 p-3 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900">{plan.limits.users}</p>
                      <p className="text-xs text-gray-500">Users</p>
                    </div>
                    <div className="text-center border-x border-gray-200">
                      <p className="text-lg font-semibold text-gray-900">{plan.limits.tasks}</p>
                      <p className="text-xs text-gray-500">Tasks</p>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-semibold text-gray-900">{plan.limits.aiRequests}</p>
                      <p className="text-xs text-gray-500">AI/day</p>
                    </div>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        {feature.included ? (
                          <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
                        )}
                        <span className={feature.included ? 'text-gray-700' : 'text-gray-400'}>
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                    onClick={() => handleSelectPlan(plan.id)}
                    data-testid={`select-${plan.id}-btn`}
                  >
                    Get Started with {plan.name}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* AI Features Deep Dive */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              AI Features That Power Your Sales
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Each AI feature is built on GPT-5.2 technology, designed specifically for intelligent task management.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {aiFeatures.map((feature) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={feature.id}
                  className="border-gray-200 hover:border-blue-200 transition-all hover:shadow-md"
                  data-testid={`ai-feature-${feature.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg flex-shrink-0 ${
                        feature.tier === 'basic' ? 'bg-blue-100' :
                        feature.tier === 'pro' ? 'bg-purple-100' : 'bg-amber-100'
                      }`}>
                        <Icon className={`w-6 h-6 ${
                          feature.tier === 'basic' ? 'text-blue-600' :
                          feature.tier === 'pro' ? 'text-purple-600' : 'text-amber-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                            {feature.name}
                          </h3>
                          <Badge className={getTierBadgeColor(feature.tier)}>
                            {feature.tier}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{feature.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {feature.benefits.map((benefit, idx) => (
                            <Badge key={idx} variant="outline" className="bg-gray-50 text-gray-600 text-xs">
                              {benefit}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-blue-600 to-purple-600 border-0 text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Outfit' }}>
                Ready to Transform Your Task Management?
              </h3>
              <p className="text-blue-100 mb-6 max-w-xl mx-auto">
                Join 500+ businesses already using AIVORA Nexus AI to boost their field team productivity by 40%.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-blue-50"
                  onClick={() => navigate('/dashboard')}
                  data-testid="start-trial-btn"
                >
                  Start Free Trial
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => toast.info('Contact sales@aivora-techlabs.com for a demo')}
                  data-testid="contact-sales-btn"
                >
                  Contact Sales
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
